import {createContext, type FC, type ReactNode, use, useCallback, useMemo, useRef, useState} from 'react'

type CommentEditsContextState = {
  // key is a unique identifier for a comment or for the body of the issue or pull
  startCommentEdit: (key: string) => void
  cancelCommentEdit: (key: string) => void
  cancelAllCommentEdits: () => void
  isCommentEditActive: () => boolean
}

const CommentEditsContext = createContext<CommentEditsContextState | null>(null)

type OngoingEditsState = Readonly<Record<string, boolean>>
const defaultEmptyState = {}

export const CommentEditsContextProvider: FC<{children: ReactNode}> = ({children}) => {
  const [ongoingEditsState, setOngoingEditsState] = useState<OngoingEditsState>(defaultEmptyState)
  const ongoingEditsStateRef = useRef<OngoingEditsState>(defaultEmptyState)

  const startCommentEdit = useCallback((key: string) => {
    if (ongoingEditsStateRef.current[key]) return

    const nextState = {...ongoingEditsStateRef.current, [key]: true}
    ongoingEditsStateRef.current = nextState
    setOngoingEditsState(nextState)
  }, [])

  const cancelCommentEdit = useCallback((key: string) => {
    if (!ongoingEditsStateRef.current[key]) return

    const nextState = {...ongoingEditsStateRef.current, [key]: false}
    ongoingEditsStateRef.current = nextState
    setOngoingEditsState(nextState)
  }, [])

  const isCommentEditActive = useCallback(() => {
    return Object.values(ongoingEditsState).some(value => value)
  }, [ongoingEditsState])

  const cancelAllCommentEdits = useCallback(() => {
    if (!Object.values(ongoingEditsStateRef.current).some(value => value)) return

    ongoingEditsStateRef.current = defaultEmptyState
    setOngoingEditsState(defaultEmptyState)
  }, [])

  const editContextValue = useMemo(() => {
    return {
      startCommentEdit,
      cancelCommentEdit,
      isCommentEditActive,
      cancelAllCommentEdits,
    }
  }, [cancelAllCommentEdits, cancelCommentEdit, isCommentEditActive, startCommentEdit])

  return <CommentEditsContext value={editContextValue}>{children}</CommentEditsContext>
}

/** For components rendered both inside and outside the provider. */
export const useOptionalCommentEditsContext = () => use(CommentEditsContext)

export const useCommentEditsContext = () => {
  const context = use(CommentEditsContext)
  if (!context) {
    throw new Error('useCommentEditsContext must be used within a CommentEditsContextProvider.')
  }

  return context
}
